// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helpers i API servisi
import { transformRoute, useTranslate } from "helpers";


import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";





// UI komponente
import { NoResults } from "ui/components/NoResults";
import { Loader } from "ui/components/Loader";

// Moduli specifični za projekt
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";


// Lokalne komponente


import { Dialog } from "ui/components/Dialog";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";
import { Errors } from "services/forms/validation/types";
import { FleetVehicleOfferRentRow, FleetVehicleOfferRentSearchFormValues } from "services/api/fleet/vehicles/offers/types";
import { VehicleOfferSaleSearchPageCmp, VehicleOfferSaleSearchPageProps } from "../types";
import { deleteFleetVehicleOfferRentAPI, getFleetVehicleOfferRentsAPI } from "services/api/fleet/vehicles/offers/api";

import VehicleOfferRentEditCard from "../../../VehicleOfferRentEditCard/VehicleOfferRentEditCard";
import { Routes as ContactRoutes } from "ui/modules/contacts/navigation";
import VehicleOfferRentSearchFilters from "../VehicleOfferRentSearchFilters/VehicleOfferRentSearchFilters";
import VehicleOfferRentSearchResults from "../VehicleOfferRentSearchResults/VehicleOfferRentSearchResults";

const VehicleOfferRentSearchPage: VehicleOfferSaleSearchPageCmp = (
    props: VehicleOfferSaleSearchPageProps
) => {
    const { id } = useParams();

    const { t } = useTranslate();

    const navigate = useNavigate();

    const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
        initialFleetVehicleDetailForm
    );

    const [searchResults, setSearchResults] = useState<
        DataWithPagination<FleetVehicleOfferRentRow>
    >(initialDataWithPagination);

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [offerRentId, setOfferRentId] = useState<string>("");
    const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<Errors>({});
    const [filters, setFilters] = useState<FleetVehicleOfferRentSearchFormValues>({
        search: null, offerRentStatusId: null, offerDateFrom: null, offerDateTo: null,
        dueDateFrom: null, dueDateTo: null, totalMin: null, totalMax: null, contactClientId: null,
    });


    useEffect(() => {
        if (id) {
            fetchVehicle(id);
            fetchSearchResult(id, searchResults.pagination, filters);
        }
    }, [id]);


    const fetchVehicle = async (id: string) => {
        if (id === undefined) {
            setFormValues(initialFleetVehicleDetailForm);
            return;
        }

        setIsLoading(true);

        let result;
        try {
            result = await getFleetVehicleAPI(id);
        } catch {
            setIsLoading(false);
            return;
        }

        setFormValues(result);
        setIsLoading(false);

        setErrors({});
    };

    const fetchSearchResult = async (id: string, pagination: Pagination, filterValues?: FleetVehicleOfferRentSearchFormValues) => {
        setIsLoading(true);
        try {
            const f = filterValues ?? filters;
            const results = await getFleetVehicleOfferRentsAPI(pagination, {
                vehicleId: id,
                offerRentStatusId: f.offerRentStatusId ?? undefined,
                search: f.search ?? undefined,
                offerDateFrom: f.offerDateFrom,
                offerDateTo: f.offerDateTo,
                dueDateFrom: f.dueDateFrom,
                dueDateTo: f.dueDateTo,
                totalMin: f.totalMin,
                totalMax: f.totalMax,
                contactClientId: f.contactClientId ?? undefined,
            });
            setSearchResults(results);
        } catch {
            setSearchResults(initialDataWithPagination);
            setIsLoading(false);
            return;
        }
        setIsLoading(false);
    };

    const handleRowClick = useCallback(async (id: string) => {
        setOfferRentId(id);
        setShowAddNewModal(true);
    }, []);

    const handleAddNew = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setOfferRentId("");
            setShowAddNewModal(true);
        },
        []
    );

    const handleEditSuccess = useCallback(() => {
        if (id) fetchSearchResult(id, searchResults.pagination);

        setOfferRentId("");
        setShowAddNewModal(false);
    }, []);

    const handleSearchChange = useCallback((formValues: FleetVehicleOfferRentSearchFormValues) => {
        setFilters(formValues);
        if (id) fetchSearchResult(id, searchResults.pagination, formValues);
    }, [id, searchResults.pagination]);

    const handleSelectedChange = useSelectedItemsChanges(
        selectedRows,
        setSelectedRows
    );

    const handlePaginationChange = (newPagination: Pagination) => {
        if (id) fetchSearchResult(id, newPagination);
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        setOfferRentId(id);
        setOpenDialog(true);
    }

    const handleOfferRentDeleteDialogConfirm = useCallback(async () => {
        if (!offerRentId || !id) return;

        try {
            const deleted = await deleteFleetVehicleOfferRentAPI(id, offerRentId, t);

            if (deleted && id) {
                await fetchSearchResult(id, searchResults.pagination);
            }

            // zatvori dijalog nakon brisanja
            setOpenDialog(false);
            setOfferRentId("");
        } catch (err) {
            console.error("Greška prilikom brisanja dokumenta:", err);
        }
    }, [offerRentId, t]);

    const handleOfferRentDeleteDialogClose = useCallback(() => {
        setOfferRentId("");
        setOpenDialog(false);
    }, []);


    const handleContactClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.preventDefault();
        event.stopPropagation();

        navigate(
            transformRoute(
                ContactRoutes.ContactDetailPageRouter,
                {
                    id: id,
                }
            )
        );
    }

    if (!id) return <></>;

    return (
        <>

            <div className="px-0 py-6 mx-[-1px] mt-[-1px] xl:px-6 border">
                <div className="text-xl font-bold text-slate-800">
                    Ponuda za najam
                </div>
                <VehicleOfferRentSearchFilters
                    onAddNew={handleAddNew}
                    onSearchChange={handleSearchChange}
                />
                {isLoading && searchResults.data.length === 0 ? (
                    <div className="relative min-h-[200px]">
                        <Loader isLoading={true}>{null}</Loader>
                    </div>
                ) : !isLoading && searchResults.pagination.totalPages === 0 ? (
                    <NoResults message="Unesite dokument klikom na 'Dodaj ponudu'" />
                ) : (
                    <VehicleOfferRentSearchResults
                        searchResults={searchResults}
                        selectedRows={selectedRows}
                        isLoading={isLoading}
                        onRowClick={handleRowClick}
                        onPaginationChange={handlePaginationChange}
                        onSelectedChange={handleSelectedChange}
                        onContactClick={handleContactClick}
                        onDeleteClick={handleDeleteClick}
                    />
                )}
            </div>

            <Dialog
                open={openDialog}
                title={t(
                    "admin.fleet.vehicle.offerrent.dialog.delete.title"
                )}
                message={t(
                    "admin.fleet.vehicle.offerrent.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.fleet.vehicle.offerrent.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.fleet.vehicle.offerrent.dialog.delete.canceltext"
                )}
                onConfirm={handleOfferRentDeleteDialogConfirm}
                onCancel={handleOfferRentDeleteDialogClose}
            />
            <VehicleOfferRentEditCard
                show={showAddNewModal}
                vehicleId={id}
                offerId={offerRentId}
                onSuccess={handleEditSuccess}
                onClose={() => setShowAddNewModal(false)}
            />
        </>
    );
};

VehicleOfferRentSearchPage.route =
    FleetVehicleRoutes.FleetVehicleOfferSearchPageRouter;

export default VehicleOfferRentSearchPage;
